import React from 'react'
import styled, { css } from 'styled-components'
import axios from 'axios'
import { connect } from 'react-redux'
import { capitalize } from 'lodash'
import { rgba } from 'styles'
import { IMAGE, UPGRADE } from 'common/endpoints'
import { POLICY_IDS } from 'common/constants'
import EmptyState from './EmptyState'
import { widgetUrl } from '../../common/endpoints'

const ccpStyles = ({ colors: c }) => ({
  main: c.main,
  mainText: c.mainInverted,

  conversation: c.mainBackground,
  conversationText: c.textMain,

  send: c.cta,
  sendText: c.mainInverted,

  sender: rgba(c.main, 0.2),
  senderText: c.textMain,
  receiver: rgba(c.main, 0.4),
  receiverText: c.textMain,
})

// original locations in DWE can be found in labels.js
const ccpTranslations = translations => {
  const t = name => translations[`ccp/${name}`]
  const basic = { noLocation: '', send: '' }
  'conversations smileys stickers textingNow typeAMessage close'.split(' ').forEach(name => (basic[name] = t(name)))

  return {
    titles: {
      warning: `! ${t('warningTitle')} !`,
      upgrade: `! ${t('blocked')} !`,
      contactInfo: `! ${t('blocked')} !`,
    },
    messages: {
      userWarning: t('userWarning'),
      upgrade: t('upgrade'),
      freeChat: t('upgrade'),
      messageLimit: t('messageLimit'),
    },
    time: { justNow: t('justNow') },
    basic,
  }
}

const config = {
  staging: {
    userUid: '3ab32395-0767-5727-a4b1-f84d3683716c',
    userToken: 'pu:3ab32395-0767-5727-a4b1-f84d3683716c:62cc69cf3b02ff6dfed4fe6f927171a9',
    identifier: '3033f445-40d6-a259-88a2-e87255091def',
  },
  test2: {
    userUid: '003fec73-4586-596b-834e-fc76afd6d3ed',
    userToken: 'pu:003fec73-4586-596b-834e-fc76afd6d3ed:b525ab82b8c62deb66a423ee67668c5b',
    identifier: '3033f445-40d6-a259-88a2-e87255091def',
  },
  hsb: {
    identifier: '08355109-18e5-42ea-9b9d-7dc76fbd381c',
    userToken: 'EX:2264647d-974c-4e31-928c-5a103e82eb75:5c78e5965f12dedf61bd3028654bd7ae',
    userUid: '2264647d-974c-4e31-928c-5a103e82eb75',
  },
  empty: {
    userUid: 'f20992ef-4812-47dc-9095-469b9205dd3a',
    identifier: '69f17d72-d0ae-41c3-918f-d019965604f8',
    userToken: 'EX:f20992ef-4812-47dc-9095-469b9205dd3a:31dbf3c7d361342329fc813ee3a45b7e',
  },
}

const StyledChatWidgetContainer = styled.div`
  transition: width 0.5s;
  flex: none;

  &:empty {
    display: none;
  }

  ${props =>
    props.mode == 'widget' &&
    css`
      width: 0;
      .inbox-conversation-open & {
        display: none;
      }
    `}

  ${props =>
    props.mode == 'sidebar' &&
    css`
      width: ${props => (props.isOpen ? 30 : 5)}rem;
      .chat-list-header {
        margin-top: 1rem;
      }
    `}
`
const siteName = location.hostname
  .replace(/^(preview\.)?(www\.)?/, '')
  .replace(/(\.\w+\.dn|(\.devel|\.branch\d)?\.preview)\.(dp-core|datingfactory)\.com/, '')

class ChatWidgetContainer extends React.Component {
  state = {};
  shouldLoadCCP = window.CCP_CONF && !this.props.flags.disableCCP;
  nodeRef = React.createRef();
  onMount = state => {
    this.setState(state)
    document.body.classList.add('chat-on', state.isOpen ? 'chat-open' : 'chat-closed')
  };

  onToggle = state => {
    this.setState(state)
    document.body.className = document.body.className.replace(
      /chat-(open|closed)/g,
      `chat-${state.isOpen ? 'open' : 'closed'}`
    )
  };

  onReady = (state, ccpUser) =>
    this.props.chatAllowed != ccpUser.premium && axios.put(UPGRADE.ccp(this.props.chatAllowed));

  onMessageSend(data) {
    const { conversation, user } = __STATE_WIDGET; // eslint-disable-line
    const { messages, meIndex } = conversation
    const oneDayAgo = Math.floor(Date.now() / 1000) - 24 * 3600
    const isMyFirstMessageForTheDay = m => m.sentBy == meIndex && m.sentAt > oneDayAgo

    // used to send "CHAT_REQUEST" emails
    data.firstMessage = !user.block && [].slice.call(messages).filter(isMyFirstMessageForTheDay).length == 1
    data.toNickname = __STATE_WIDGET.conversation.otherUserProfile.profileUrl.match('.+/(.+)')[1]; // eslint-disable-line

    axios.post('/api/ccp/user/interaction', data)
  }

  getUpgradeUrl(profile) {
    const nickname = ((profile || {}).profileUrl || '').replace(/members|\//g, '')
    return UPGRADE.page(POLICY_IDS.CHAT, nickname)
  }

  updateMode = () => {
    const mode = window.innerWidth >= 1280 ? 'sidebar' : 'widget'
    if (mode != this.state.mode) {
      this.setState({ mode })
    }
  };

  setCcpLoadedState = () => {
    this.setState({ ccpLoaded: true })
  };

  componentDidMount() {
    this.updateMode()
    if (this.shouldLoadCCP) {
      this.injectCcpScript()
      document.addEventListener('ccp-widget-loaded', this.setCcpLoadedState)
    }
    window.addEventListener('resize', this.updateMode)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateMode)
    document.removeEventListener('ccp-widget-loaded', this.setCcpLoadedState)
  }

  injectCcpScript = () => {
    let widgetScript = document.getElementById('ccp-widget-script')

    if (!widgetScript) {
      const widgetScript = document.createElement('script')
      widgetScript.id = 'ccp-widget-script'
      //Can be replaced due to development to the local https hosted widget.
      widgetScript.src = widgetUrl
      document.head.appendChild(widgetScript)
    }
  };

  loadCcp = options => {
    if (this.nodeRef.current && window.CCP) {
      if (window.CCP.initWidget) {
        window.CCP.initWidget(this.nodeRef.current, options)
      }
    }
  };

  render() {
    const { isOpen, mode } = this.state
    const { stickers, styles, translations, lc } = this.props
    const { ccpLoaded } = this.state
    return ccpLoaded ? (
      <StyledChatWidgetContainer isOpen={isOpen} mode={mode} className="ccp-chat-container" ref={this.nodeRef}>
        {this.loadCcp({
          mode,
          stickers,
          siteName: siteName == 'localhost' ? 'nookybuddies.com' : siteName,
          urls: {
            sticker: IMAGE.sticker,
            upgrade: this.getUpgradeUrl,
            sound: name => `https://widget.thedatingmessenger.com/ccp-widget/assets/sounds/${name}.mp3`,
          },
          hooks: {
            onMount: this.onMount,
            onToggle: this.onToggle,
            onReady: this.onReady,
            onMessageSend: this.onMessageSend,
            onClickOutside: () => mode == 'widget' && isOpen && window.CCP.setOpen(false),
          },
          features: {
            emptyState: () => <EmptyState />,
            intl: lc,
            virtualHottie: true,
          },
          styles: ccpStyles(styles),
          translations: ccpTranslations(translations),
        })}
      </StyledChatWidgetContainer>
    ) : null
  }
}

export default connect(({ account, myProfile, flags, siteConfig: { styles, supportedLanguages }, translations }) => {
  const { chatAllowed, members } = myProfile
  const { fakeCCP, disableCCP, adult } = flags
  const ccpConf = disableCCP ? null : fakeCCP ? config.hsb : myProfile.ccpConf

  const sex = members[0].sexOfMemberId == 2 ? 'woman' : 'man'
  const gay = ![1, 4].includes(members[0].orientationId)
  const stickers = adult ? ['common', sex].concat(gay ? ['gay' + capitalize(sex)] : []) : []

  if (ccpConf !== window.CCP_CONF) {
    window.CCP_CONF = ccpConf
    window.__STATE_WIDGET = null
    let ccpConf = window.CCP_CONF || {}
    ccpConf['translations'] = {
      virtualHottie: {
        virtualHottie: 'Cyber Hottie',
        chattingWithHottie: 'Hi, you are chatting with the cyber hottie',
        automatedProfile: 'I\'m an Automated Profile. For more information on Automated Profiles, please refer to the',
        termsAndConditions: 'Terms And Conditions',
      },
    }
    Object.assign(window.CONFIG || {}, ccpConf)
  }
  const lc = supportedLanguages.find(e => e.id == account.main.lc)?.isoCode

  return { ccpConf, stickers, flags: { fakeCCP, disableCCP }, styles, chatAllowed, translations, lc }
})(ChatWidgetContainer)
